import { usePortalTranslate } from '@/hooks'
import { Reason } from '@/domain/return-reasons'
import { Base } from '@itsrever/design-system'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

type ReasonProps = {
  index?: number
  subIndex?: number
  reason: Reason
  changeValues: (values: string[], ind?: number) => void
  changeMandatoryComment: (value: boolean, ind?: number) => void
  readOnly?: boolean
  isShopify: boolean
}

// Fixed widths for the switch columns: the header and each row are separate
// grid containers, so auto columns would size to their own content and the
// switches would not line up under their column labels.
export const reasonColumnsClassName =
  'grid grid-cols-[minmax(0,1fr)_6rem_12rem] items-center gap-x-4'

export const shopifyReasonColumnsClassName =
  'grid grid-cols-[minmax(0,1fr)_6rem_12rem_minmax(0,20rem)] items-center gap-x-4'

export const ReturnReason: React.FC<ReasonProps> = ({
  index,
  subIndex,
  reason,
  changeValues,
  changeMandatoryComment,
  readOnly,
  isShopify
}) => {
  const { t } = useTranslation()
  const { trans } = usePortalTranslate({})
  const switchId = useId()

  const isActive = reason.categoryIds.some((id) => id)
  const position = typeof index === 'number' ? index : subIndex

  const handleChangeCategoryIds = (values: string[]) => {
    changeValues(values.length ? values : ['*'], position)
  }

  const handleToggleSwitch = () => {
    changeValues(isActive ? [] : ['*'], position)
  }

  const handleToggleMandatoryComment = () => {
    changeMandatoryComment(!reason.mandatoryComment, position)
  }

  const productTypes = reason.categoryIds.filter(
    (id) => id !== '*' && id !== ''
  )
  const label = trans(`select_reason_${reason.reason}`)

  const mandatoryCommentSwitch = (
    <Base.Switch
      className="justify-self-center"
      aria-label={t('edit_reasons.mandatory_comment_for', { reason: label })}
      disabled={readOnly}
      checked={reason.mandatoryComment}
      onCheckedChange={handleToggleMandatoryComment}
    />
  )

  if (isShopify)
    return (
      <li className={`${shopifyReasonColumnsClassName} py-3`}>
        <label
          htmlFor={switchId}
          className="cursor-pointer text-sm text-neutral-900"
        >
          {label}
        </label>
        <Base.Switch
          id={switchId}
          className="justify-self-center"
          disabled={readOnly}
          checked={isActive}
          onCheckedChange={handleToggleSwitch}
        />
        {mandatoryCommentSwitch}
        {isActive ? (
          <Base.TagInput
            values={productTypes}
            onChange={handleChangeCategoryIds}
            // "Always shown" only holds while the reason has no product types.
            placeholder={
              productTypes.length ? undefined : t('edit_reasons.enabled_reason')
            }
            readOnly={readOnly}
            removeLabel={(value) => t('tag_input.remove', { value })}
          />
        ) : (
          <div />
        )}
      </li>
    )

  return (
    <li className={`${reasonColumnsClassName} py-3`}>
      <label
        htmlFor={switchId}
        className="cursor-pointer text-sm text-neutral-900"
      >
        {label}
      </label>
      <Base.Switch
        id={switchId}
        className="justify-self-center"
        disabled={readOnly}
        checked={isActive}
        onCheckedChange={handleToggleSwitch}
      />
      {mandatoryCommentSwitch}
    </li>
  )
}

export default ReturnReason
